import { Span, Range, Editor, Path, Node } from 'slate';
import { isVoidNode } from './isVoidNode.mjs';

/**
 * Match the object with a predicate object or function.
 * If predicate is:
 * - object: every predicate key/value should be in obj.
 * - function: it should return true.
 */
function match(obj, predicate) {
  if (!predicate) return true;
  if (typeof predicate === 'object') {
    return Object.entries(predicate).every(([key, value]) => {
      const values = Array.isArray(value) ? value : [value];
      return values.includes(obj[key]);
    });
  }
  return predicate(obj);
}

/**
 * Find node matching the condition.
 */
function findNode(editor, options) {
  // Slate throws when things aren't found so we wrap in a try catch and return undefined on throw.
  try {
    const {
      match: _match = () => true,
      at = editor.selection || [],
      reverse = false,
      voids = false
    } = options;
    let from;
    let to;
    if (Span.isSpan(at)) {
      [from, to] = at;
    } else if (Range.isRange(at)) {
      const first = Editor.path(editor, at, {
        edge: 'start'
      });
      const last = Editor.path(editor, at, {
        edge: 'end'
      });
      from = reverse ? last : first;
      to = reverse ? first : last;
    }
    let root = [editor, []];
    if (Path.isPath(at)) {
      root = Editor.node(editor, at);
    }
    const nodeEntries = Node.nodes(root[0], {
      reverse,
      from,
      to,
      pass: ([node]) => voids ? false : isVoidNode(editor, node)
    });
    for (const [node, path] of nodeEntries) {
      if (match(node, _match)) {
        return [node, path];
      }
    }
  } catch (error) {
    return undefined;
  }
  return undefined;
}
function findNodes(editor, {
  match
}) {
  const matches = [];
  for (const entry of Node.nodes(editor)) {
    const [node] = entry;
    if (match(node)) {
      matches.push(entry);
    }
  }
  return matches;
}

// Query for Editor.nodes can throw if the document is not finished being normalized
// during multi-pass normalization, for more details, refer to:
// https://github.com/ianstormtaylor/slate/blob/main/docs/concepts/11-normalizing.md#multi-pass-normalizing
function findAllNodesSafe(editor, options) {
  try {
    return Array.from(Editor.nodes(editor, options));
  } catch (error) {
    return [];
  }
}

export { findAllNodesSafe, findNode, findNodes, match };
